import { useEffect } from "react";
import { SITE_NAME, DEFAULT_OG_IMAGE, absUrl } from "../lib/seo";

const DEFAULT_TITLE = "Suvikka Sakhi | Women's Fashion, Beauty & Lifestyle Community";
const DEFAULT_DESC =
  "Discover Suvikka Sakhi — a women's community for fashion, beauty, jewellery and lifestyle. Connect, create, join challenges and be part of something meaningful.";

function setMeta(attr, key, content) {
  if (content == null) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.setAttribute("data-seo", "1");
  return el;
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"][data-seo="1"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

/**
 * Declarative SEO head manager for the SPA.
 * Sets title, meta description, canonical, Open Graph, Twitter cards,
 * robots directive and JSON-LD structured data.
 */
export default function Seo({
  title,
  description,
  canonicalPath,
  image,
  type = "website",
  noindex = false,
  jsonLd,
}) {
  useEffect(() => {
    const fullTitle = title ? `${title}` : DEFAULT_TITLE;
    const desc = description || DEFAULT_DESC;
    const canonical = absUrl(canonicalPath || window.location.pathname);
    const ogImage = image || DEFAULT_OG_IMAGE;

    document.title = fullTitle;
    setMeta("name", "description", desc);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    setLink("canonical", canonical);

    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:type", type);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", ogImage);

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", ogImage);

    const blocks = (Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []).filter(Boolean);
    document.head.querySelectorAll('script[data-seo-ld="1"]').forEach((n) => n.remove());
    blocks.forEach((data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-ld", "1");
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
    });

    return () => {
      document.head.querySelectorAll('script[data-seo-ld="1"]').forEach((n) => n.remove());
    };
  }, [title, description, canonicalPath, image, type, noindex, JSON.stringify(jsonLd)]);

  return null;
}
